import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import "./TermsAndConditions.css";
import {
    ShieldCheck,
    FileText,
    Wifi,
    CheckCircle,
    AlertTriangle,
    CreditCard,
    Phone,
    Mail,
    Globe,
    Clock,
    Lock,
    Scale,
    HelpCircle,
    Zap,
    RefreshCw,
    Server,
    UserCheck,
    Ban,
    ArrowUpRight
} from "lucide-react";

/**
 * TermsAndConditions Component
 * Production-ready Terms & Conditions page for RiTU Broadband ISP (India).
 * Features:
 * - ISP Branding in #0B72B9 Blue theme
 * - Sticky Table of Contents navigation on desktop
 * - Modern section cards with numbered headings & icons
 * - Acceptable Use & Important Notice highlight boxes
 * - Razorpay, FUP, Payment & Outage Disclaimers
 * - Customer Support Contact Card
 */
function TermsAndConditions() {
    const [activeSection, setActiveSection] = useState("sec-1");

    // Table of contents navigation list
    const tocItems = [
        { id: "sec-1", title: "1. Introduction", icon: <FileText size={16} /> },
        { id: "sec-2", title: "2. Eligibility", icon: <UserCheck size={16} /> },
        { id: "sec-3", title: "3. User Registration", icon: <CheckCircle size={16} /> },
        { id: "sec-4", title: "4. Broadband Service", icon: <Wifi size={16} /> },
        { id: "sec-5", title: "5. Installation Policy", icon: <Zap size={16} /> },
        { id: "sec-6", title: "6. Plans & Pricing", icon: <RefreshCw size={16} /> },
        { id: "sec-7", title: "7. Payments & Billing", icon: <CreditCard size={16} /> },
        { id: "sec-8", title: "8. Refund & Cancellation", icon: <RefreshCw size={16} /> },
        { id: "sec-9", title: "9. Service Suspension", icon: <Ban size={16} /> },
        { id: "sec-10", title: "10. Fair Usage Policy (FUP)", icon: <Server size={16} /> },
        { id: "sec-11", title: "11. User Responsibilities", icon: <AlertTriangle size={16} /> },
        { id: "sec-12", title: "12. Privacy & Data Protection", icon: <Lock size={16} /> },
        { id: "sec-13", title: "13. Third-Party Services", icon: <CreditCard size={16} /> },
        { id: "sec-14", title: "14. Maintenance & Downtime", icon: <Clock size={16} /> },
        { id: "sec-15", title: "15. Termination of Service", icon: <Ban size={16} /> },
        { id: "sec-16", title: "16. Limitation of Liability", icon: <ShieldCheck size={16} /> },
        { id: "sec-17", title: "17. Support & Governing Law", icon: <Scale size={16} /> },
        { id: "sec-18", title: "18. Contact Information", icon: <Phone size={16} /> },
    ];

    // ScrollSpy to highlight active TOC item on scroll
    useEffect(() => {
        const handleScroll = () => {
            const sections = tocItems.map(item => document.getElementById(item.id));
            const scrollPosition = window.scrollY + 180;

            for (let i = sections.length - 1; i >= 0; i--) {
                const section = sections[i];
                if (section && section.offsetTop <= scrollPosition) {
                    setActiveSection(tocItems[i].id);
                    break;
                }
            }
        };

        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    // Smooth scroll handler
    const scrollToSection = (id) => {
        setActiveSection(id);
        const element = document.getElementById(id);
        if (element) {
            const yOffset = -100;
            const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
            window.scrollTo({ top: y, behavior: "smooth" });
        }
    };

    return (
        <div className="terms-page-root">
            <Header />

            {/* Hero Header Section */}
            <div className="terms-hero">
                <div className="terms-hero-container">
                    <div className="terms-hero-badge">
                        <ShieldCheck size={16} /> Official Legal Document
                    </div>
                    <h1>Terms &amp; Conditions</h1>
                    <p className="terms-hero-subtitle">
                        Please read these Terms of Service carefully before subscribing to or using broadband and internet services provided by <strong>RiTU Broadband</strong>.
                    </p>

                    <div className="terms-meta-bar">
                        <span className="meta-item">
                            <Clock size={15} /> <strong>Last Updated:</strong> August 2026
                        </span>
                        <span className="meta-divider">•</span>
                        <span className="meta-item">
                            <Globe size={15} /> <strong>Applicable Region:</strong> India
                        </span>
                    </div>
                </div>
            </div>

            {/* Main Content Layout */}
            <div className="terms-container">
                {/* Left Column: Sticky Table of Contents (Desktop) */}
                <aside className="terms-sidebar">
                    <div className="toc-card">
                        <h3 className="toc-title">
                            <FileText size={18} /> Table of Contents
                        </h3>
                        <nav className="toc-nav">
                            {tocItems.map((item) => (
                                <button
                                    key={item.id}
                                    className={`toc-link ${activeSection === item.id ? "active" : ""}`}
                                    onClick={() => scrollToSection(item.id)}
                                >
                                    <span className="toc-icon">{item.icon}</span>
                                    <span className="toc-text">{item.title}</span>
                                </button>
                            ))}
                        </nav>
                    </div>
                </aside>

                {/* Right Column: Terms Content Cards */}
                <main className="terms-content">

                    {/* Notice Callout Box */}
                    <div className="notice-box important-notice">
                        <div className="notice-header">
                            <AlertTriangle className="notice-icon" size={22} />
                            <h4>Important Notice &amp; Summary</h4>
                        </div>
                        <p>
                            By registering for, accessing, or using broadband services provided by <strong>RiTU Broadband</strong>, you acknowledge that you have read, understood, and agreed to be legally bound by these Terms and Conditions. All payments are processed securely via <strong>Razorpay</strong>. Subscriptions activate only after payment confirmation and automatically expire upon billing cycle completion.
                        </p>
                    </div>

                    {/* Section 1 */}
                    <section id="sec-1" className="terms-card">
                        <div className="card-header-row">
                            <span className="section-number">01</span>
                            <div className="section-title-wrap">
                                <h2>1. Introduction</h2>
                                <span className="section-subtitle">Scope &amp; Acceptance of Terms</span>
                            </div>
                        </div>
                        <div className="card-body-content">
                            <p>
                                Welcome to <strong>RiTU Broadband</strong> ("Company", "We", "Us", or "Our"). These Terms and Conditions govern your access to and use of our fiber-optic broadband internet connection, digital TV/OTT bundle services, customer portal, mobile interface, and associated hardware located at <a href="https://ritubroadband.in" target="_blank" rel="noopener noreferrer" className="terms-inline-link">https://ritubroadband.in</a> ("Website").
                            </p>
                            <p>
                                By applying for a new connection, subscribing to a plan, or making a payment, you ("Customer", "Subscriber", or "User") agree to comply strictly with all clauses defined herein along with the directives of the Telecom Regulatory Authority of India (TRAI) and Department of Telecommunications (DoT), Government of India.
                            </p>
                        </div>
                    </section>

                    {/* Section 2 */}
                    <section id="sec-2" className="terms-card">
                        <div className="card-header-row">
                            <span className="section-number">02</span>
                            <div className="section-title-wrap">
                                <h2>2. Eligibility Criteria</h2>
                                <span className="section-subtitle">Subscriber Verification Requirements</span>
                            </div>
                        </div>
                        <div className="card-body-content">
                            <p>
                                To subscribe to RiTU Broadband services, you must satisfy the following legal requirements:
                            </p>
                            <ul className="terms-styled-list">
                                <li>You must be at least <strong>18 years of age</strong> or legally competent under the Indian Contract Act, 1872.</li>
                                <li>You must reside within our active fiber coverage area in Achampet and surrounding serviced regions.</li>
                                <li>You must submit mandatory <strong>Know Your Customer (KYC)</strong> verification documents as per DoT guidelines, including a valid government photo ID (Aadhaar Card, Voter ID, Passport, or Driving License) and proof of installation address.</li>
                            </ul>
                        </div>
                    </section>

                    {/* Section 3 */}
                    <section id="sec-3" className="terms-card">
                        <div className="card-header-row">
                            <span className="section-number">03</span>
                            <div className="section-title-wrap">
                                <h2>3. User Account &amp; Registration</h2>
                                <span className="section-subtitle">Credential Security &amp; Accuracy</span>
                            </div>
                        </div>
                        <div className="card-body-content">
                            <p>
                                Upon registering a subscriber account, you agree to provide complete, current, and accurate personal information. You are solely responsible for maintaining the confidentiality of your login credentials (username, password, OTPs).
                            </p>
                            <p>
                                Any activity conducted under your registered account is deemed to be authorized by you. If you suspect unauthorized access or compromise of your credentials, you must notify RiTU Broadband support immediately at <a href="mailto:support@ritubroadband.in" className="terms-inline-link">support@ritubroadband.in</a>.
                            </p>
                        </div>
                    </section>

                    {/* Section 4 */}
                    <section id="sec-4" className="terms-card">
                        <div className="card-header-row">
                            <span className="section-number">04</span>
                            <div className="section-title-wrap">
                                <h2>4. Broadband Service &amp; Speeds</h2>
                                <span className="section-subtitle">Fiber Network Performance Spectrum</span>
                            </div>
                        </div>
                        <div className="card-body-content">
                            <p>
                                RiTU Broadband provides high-speed fiber-to-the-home (FTTH) internet connection. Promoted speeds (e.g., up to 100 Mbps or 200 Mbps) represent maximum theoretical network capacity measured up to the Optical Network Terminal (ONT).
                            </p>
                            <p>
                                Actual end-user speeds experienced on devices may fluctuate due to factors including device Wi-Fi standard limits, distance from router, internal wiring, concurrent device load, server capacity of third-party websites, and overhead network protocols.
                            </p>
                        </div>
                    </section>

                    {/* Section 5 */}
                    <section id="sec-5" className="terms-card">
                        <div className="card-header-row">
                            <span className="section-number">05</span>
                            <div className="section-title-wrap">
                                <h2>5. Installation &amp; Equipment Policy</h2>
                                <span className="section-subtitle">ONT Router &amp; Wiring Ownership</span>
                            </div>
                        </div>
                        <div className="card-body-content">
                            <ul className="terms-styled-list">
                                <li><strong>Equipment Ownership:</strong> The Optical Network Terminal (ONT / Wi-Fi Router) and optical drop cables provided during installation remain the exclusive property of RiTU Broadband, unless explicitly purchased under a full-sale agreement.</li>
                                <li><strong>Care &amp; Custody:</strong> The subscriber must safeguard the ONT device against physical damage, liquid exposure, power surges, or unauthorized tampering.</li>
                                <li><strong>Return on Termination:</strong> Upon termination or deactivation of service, the subscriber must return all company-owned hardware in good working condition. Damages will incur replacement charges.</li>
                            </ul>
                        </div>
                    </section>

                    {/* Section 6 */}
                    <section id="sec-6" className="terms-card">
                        <div className="card-header-row">
                            <span className="section-number">06</span>
                            <div className="section-title-wrap">
                                <h2>6. Plans, Pricing &amp; Auto-Expiry</h2>
                                <span className="section-subtitle">Tariff Structure &amp; Billing Cycles</span>
                            </div>
                        </div>
                        <div className="card-body-content">
                            <p>
                                Tariff plans are structured on a prepaid monthly or multi-month billing cycle. All prices are listed in Indian Rupees (INR) and are subject to applicable taxes (including GST) unless explicitly stated as tax-inclusive.
                            </p>
                            <div className="notice-box highlight-notice">
                                <div className="notice-header">
                                    <Clock className="notice-icon" size={20} />
                                    <h4>Automatic Plan Expiry &amp; Recharge Policy</h4>
                                </div>
                                <p>
                                    <strong>Plans become active strictly only after a successful payment transaction.</strong> Monthly plans expire automatically at 23:59 IST on the final day of the valid cycle (typically 30 days). Subscribers must perform a timely recharge before expiry to avoid service interruption.
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* Section 7 */}
                    <section id="sec-7" className="terms-card">
                        <div className="card-header-row">
                            <span className="section-number">07</span>
                            <div className="section-title-wrap">
                                <h2>7. Payments &amp; Razorpay Gateway</h2>
                                <span className="section-subtitle">Secure Online Transaction Directives</span>
                            </div>
                        </div>
                        <div className="card-body-content">
                            <p>
                                Online payment recharges on <a href="https://ritubroadband.in" className="terms-inline-link">https://ritubroadband.in</a> are securely processed through <strong>Razorpay Payment Gateway</strong>, supporting UPI (Google Pay, PhonePe, Paytm), Credit Cards, Debit Cards, Net Banking, and Authorized Wallets.
                            </p>
                            <ul className="terms-styled-list">
                                <li>RiTU Broadband does not store your private banking passwords, PINs, or CVV numbers.</li>
                                <li>Payments are considered successful only upon receipt of official transaction confirmation from Razorpay and your issuing bank.</li>
                                <li>If money is debited from your bank account but payment status shows pending or failed, the amount is typically refunded automatically by your bank within 3 to 7 working days.</li>
                            </ul>
                        </div>
                    </section>

                    {/* Section 8 */}
                    <section id="sec-8" className="terms-card">
                        <div className="card-header-row">
                            <span className="section-number">08</span>
                            <div className="section-title-wrap">
                                <h2>8. Refund &amp; Cancellation Policy</h2>
                                <span className="section-subtitle">Terms for Service Discontinuation</span>
                            </div>
                        </div>
                        <div className="card-body-content">
                            <p>
                                Pre-paid subscription recharges are non-refundable once the plan billing cycle has commenced and service activation is confirmed.
                            </p>
                            <p>
                                <strong>Technical Infeasibility Refund:</strong> If a new connection request cannot be fulfilled due to technical infeasibility or fiber unreachability prior to installation, any advance deposit paid will be refunded to the original payment source within 7 to 10 business days.
                            </p>
                        </div>
                    </section>

                    {/* Section 9 */}
                    <section id="sec-9" className="terms-card">
                        <div className="card-header-row">
                            <span className="section-number">09</span>
                            <div className="section-title-wrap">
                                <h2>9. Service Suspension &amp; Right to Discontinue</h2>
                                <span className="section-subtitle">Conditions for Immediate Disconnection</span>
                            </div>
                        </div>
                        <div className="card-body-content">
                            <p>
                                RiTU Broadband reserves the absolute right to suspend or terminate services without prior notice in the event of:
                            </p>
                            <ul className="terms-styled-list">
                                <li>Non-payment or expiration of active prepaid subscription plans.</li>
                                <li>Breach of any clause defined in these Terms and Conditions or Indian telecom law.</li>
                                <li>Directives received from law enforcement, TRAI, DoT, or judicial authority.</li>
                                <li>Detection of suspicious, fraudulent, or illegal network activity originating from the subscriber’s connection.</li>
                            </ul>
                        </div>
                    </section>

                    {/* Section 10 - FUP */}
                    <section id="sec-10" className="terms-card">
                        <div className="card-header-row">
                            <span className="section-number">10</span>
                            <div className="section-title-wrap">
                                <h2>10. Internet Fair Usage Policy (FUP)</h2>
                                <span className="section-subtitle">Bandwidth Allocation &amp; Network Health</span>
                            </div>
                        </div>
                        <div className="card-body-content">
                            <p>
                                To ensure equitable high-speed internet quality for all subscribers on our shared optical network, unlimited data plans are governed by a standard Fair Usage Policy (FUP) cap aligned with TRAI guidelines (typically 3,300 GB / 3.3 TB per billing cycle).
                            </p>
                            <p>
                                Upon exceeding the designated FUP threshold within a single billing cycle, internet access will remain available at a reduced speed (e.g., 2 Mbps to 5 Mbps) until the next monthly billing cycle commences.
                            </p>
                        </div>
                    </section>

                    {/* Section 11 - Acceptable Use */}
                    <section id="sec-11" className="terms-card">
                        <div className="card-header-row">
                            <span className="section-number">11</span>
                            <div className="section-title-wrap">
                                <h2>11. User Responsibilities &amp; Prohibited Uses</h2>
                                <span className="section-subtitle">Acceptable Use Policy (AUP)</span>
                            </div>
                        </div>
                        <div className="card-body-content">

                            <div className="notice-box acceptable-use-box">
                                <div className="notice-header">
                                    <Ban className="notice-icon" size={20} />
                                    <h4>Strictly Prohibited Activities</h4>
                                </div>
                                <p>
                                    Subscribers are strictly prohibited from engaging in any illegal internet activity. Violation will result in immediate service termination and referral to Cyber Crime authorities.
                                </p>
                            </div>

                            <ul className="terms-styled-list">
                                <li><strong>Prohibition of Reselling:</strong> You shall not resell, redistribute, or share your home broadband connection with neighboring premises or multiple commercial entities without express written authorization from RiTU Broadband.</li>
                                <li><strong>Illegal Activities:</strong> Hosting, transmitting, or downloading content related to child exploitation, terrorism, pirated copyrighted media, malware, phishing, or anti-national material under the Information Technology Act, 2000 is illegal.</li>
                                <li><strong>Network Interference:</strong> Running unauthorized commercial web servers, mass spamming, port scanning, or initiating Denial of Service (DoS) attacks is forbidden.</li>
                            </ul>
                        </div>
                    </section>

                    {/* Section 12 */}
                    <section id="sec-12" className="terms-card">
                        <div className="card-header-row">
                            <span className="section-number">12</span>
                            <div className="section-title-wrap">
                                <h2>12. Privacy &amp; Data Collection</h2>
                                <span className="section-subtitle">Information Handling Directives</span>
                            </div>
                        </div>
                        <div className="card-body-content">
                            <p>
                                We collect and process personal data (Name, Address, Mobile Number, Email, Aadhaar/KYC ID, IP Logs) strictly for account provisioning, billing, customer service, and statutory compliance with the Information Technology Act and DoT directives.
                            </p>
                            <p>
                                RiTU Broadband does not sell or rent subscriber personal information to unauthorized third-party advertisers. IP allocation and connection logs are retained as required by Indian telecommunication law.
                            </p>
                        </div>
                    </section>

                    {/* Section 13 */}
                    <section id="sec-13" className="terms-card">
                        <div className="card-header-row">
                            <span className="section-number">13</span>
                            <div className="section-title-wrap">
                                <h2>13. Third-Party Services &amp; OTT Bundles</h2>
                                <span className="section-subtitle">External Content Subscriptions</span>
                            </div>
                        </div>
                        <div className="card-body-content">
                            <p>
                                Subscription plans that include bundled OTT platforms (e.g., Netflix, Prime Video, JioHotstar, Zee5, SonyLIV, ETV Win, SunNXT) are subject to the respective third-party service provider's terms of service and content availability.
                            </p>
                            <p>
                                RiTU Broadband acts solely as a billing aggregator for bundled OTT apps and is not responsible for third-party app crashes, content removal, or account login issues managed directly by the OTT platforms.
                            </p>
                        </div>
                    </section>

                    {/* Section 14 */}
                    <section id="sec-14" className="terms-card">
                        <div className="card-header-row">
                            <span className="section-number">14</span>
                            <div className="section-title-wrap">
                                <h2>14. Maintenance, Downtime &amp; Force Majeure</h2>
                                <span className="section-subtitle">Outage &amp; Interruption Disclaimer</span>
                            </div>
                        </div>
                        <div className="card-body-content">
                            <p>
                                RiTU Broadband strives to maintain an industry-standard 99.9% uptime. However, routine emergency maintenance, fiber upgrades, and trunk line repairs may occasionally cause temporary service disruption.
                            </p>
                            <div className="notice-box highlight-notice">
                                <div className="notice-header">
                                    <Server className="notice-icon" size={20} />
                                    <h4>Force Majeure Outage Exclusion</h4>
                                </div>
                                <p>
                                    <strong>RiTU Broadband is not liable for service interruptions caused by events beyond reasonable control</strong>, including but not limited to: severe weather, electrical grid blackouts, natural disasters, municipal road work fiber cuts, government Internet shutdowns, civil unrest, or upstream tier-1 optical fiber link failures.
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* Section 15 */}
                    <section id="sec-15" className="terms-card">
                        <div className="card-header-row">
                            <span className="section-number">15</span>
                            <div className="section-title-wrap">
                                <h2>15. Termination of Service</h2>
                                <span className="section-subtitle">Account Surrender &amp; Deactivation</span>
                            </div>
                        </div>
                        <div className="card-body-content">
                            <p>
                                Subscribers may request voluntary deactivation of their broadband connection by submitting a formal written request or contacting customer support at least 7 days prior to their next renewal cycle.
                            </p>
                            <p>
                                Upon termination, any outstanding balances must be cleared immediately, and all company equipment (Wi-Fi router/ONT) must be surrendered to our technical representative.
                            </p>
                        </div>
                    </section>

                    {/* Section 16 */}
                    <section id="sec-16" className="terms-card">
                        <div className="card-header-row">
                            <span className="section-number">16</span>
                            <div className="section-title-wrap">
                                <h2>16. Limitation of Liability</h2>
                                <span className="section-subtitle">Financial &amp; Damages Cap</span>
                            </div>
                        </div>
                        <div className="card-body-content">
                            <p>
                                To the maximum extent permitted under applicable law, RiTU Broadband and its affiliates shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, business data, or commercial opportunities arising out of internet downtime or speed variations.
                            </p>
                            <p>
                                In all events, the total cumulative liability of RiTU Broadband for any direct claims shall not exceed the monthly subscription amount paid by the customer for the specific affected billing cycle.
                            </p>
                        </div>
                    </section>

                    {/* Section 17 */}
                    <section id="sec-17" className="terms-card">
                        <div className="card-header-row">
                            <span className="section-number">17</span>
                            <div className="section-title-wrap">
                                <h2>17. Customer Support, Modifications &amp; Governing Law</h2>
                                <span className="section-subtitle">Legal Jurisdiction &amp; Amendments</span>
                            </div>
                        </div>
                        <div className="card-body-content">
                            <p>
                                <strong>Changes to Terms:</strong> RiTU Broadband reserves the right to modify these Terms and Conditions at any time. Updated terms will be published on our official website (<a href="https://ritubroadband.in" className="terms-inline-link">https://ritubroadband.in</a>). Continued use of service after modifications implies acceptance.
                            </p>
                            <p>
                                <strong>Governing Law:</strong> These terms are governed by and construed in accordance with the laws of India. Any legal disputes arising out of these terms shall be subject to the exclusive jurisdiction of the competent courts in Telangana, India.
                            </p>
                        </div>
                    </section>

                    {/* Section 18 - Contact Card */}
                    <section id="sec-18" className="terms-card contact-card-section">
                        <div className="card-header-row">
                            <span className="section-number">18</span>
                            <div className="section-title-wrap">
                                <h2>18. Contact &amp; Grievance Redressal</h2>
                                <span className="section-subtitle">Official ISP Communication Desk</span>
                            </div>
                        </div>
                        <div className="card-body-content">
                            <p>
                                If you have any questions, billing queries, technical complaints, or legal inquiries regarding these Terms &amp; Conditions, please reach out to our team:
                            </p>

                            <div className="official-contact-grid">
                                <div className="contact-box">
                                    <div className="contact-box-icon"><Globe size={22} /></div>
                                    <div className="contact-box-info">
                                        <span className="contact-box-label">Official Website</span>
                                        <a href="https://ritubroadband.in" target="_blank" rel="noopener noreferrer" className="contact-box-val">
                                            https://ritubroadband.in <ArrowUpRight size={14} />
                                        </a>
                                    </div>
                                </div>

                                <div className="contact-box">
                                    <div className="contact-box-icon"><Mail size={22} /></div>
                                    <div className="contact-box-info">
                                        <span className="contact-box-label">Email Support</span>
                                        <a href="mailto:support@ritubroadband.in" className="contact-box-val">
                                            support@ritubroadband.in
                                        </a>
                                    </div>
                                </div>

                                <div className="contact-box">
                                    <div className="contact-box-icon"><Phone size={22} /></div>
                                    <div className="contact-box-info">
                                        <span className="contact-box-label">Primary Support Phone</span>
                                        <a href="tel:7702935830" className="contact-box-val">
                                            +91 7702935830
                                        </a>
                                    </div>
                                </div>

                                <div className="contact-box">
                                    <div className="contact-box-icon"><Phone size={22} /></div>
                                    <div className="contact-box-info">
                                        <span className="contact-box-label">Secondary Support Phone</span>
                                        <a href="tel:8500168970" className="contact-box-val">
                                            +91 8500168970
                                        </a>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </section>

                    {/* Bottom Acceptance Banner */}
                    <div className="terms-bottom-banner">
                        <div className="banner-icon-badge">
                            <CheckCircle size={24} />
                        </div>
                        <div className="banner-text">
                            <h3>Acknowledgement &amp; Agreement</h3>
                            <p>"I have read and agree to the Terms &amp; Conditions of RiTU Broadband."</p>
                        </div>
                        <Link to="/" className="banner-home-btn">
                            Return to Home Page
                        </Link>
                    </div>

                </main>
            </div>

            <Footer />
        </div>
    );
}

export default TermsAndConditions;
